import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Spinner,
  Icon,
  IconButton,
  Collapse,
  Badge,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Select,
  useDisclosure,
} from "@chakra-ui/react";
import {
  CloseIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import {
  FaTruck,
  FaArrowRight,
  FaWalking,
  FaExclamationTriangle,
  FaPrint,
} from "react-icons/fa";
import { IoMdPricetag } from "react-icons/io";
import { useRef, useState, useEffect, useMemo } from "react";
import { GARAAZ_PERSONNEL_SUB_TYPES } from "../../../types";
import { DeliveryModes } from "../../../types";
import { useToast } from "@chakra-ui/react";
import { getGrazeons } from "../services";
import { RiderDetails } from "../types";
import { GroupSelection, useDispatchDashboardStore } from "../store/useDispatchDashboardStore";
import {
  InvoiceMediaIdentifier,
  normalizeInvoiceMediaId,
  printGaraazInvoices,
} from "../../../utils/invoicePrintUtils";
import { extractInvoiceMediaIdsFromGroup } from "../../../utils/invoiceExtractionUtils";
import {
  handleInvoicePrintError,
  showInvoicePrintSuccess,
} from "../../../utils/invoicePrintErrorHandler";
import type { HopDestinationPayload } from "../../../utils/hopDestination";
import {
  areAllOrdersLockedToCustomerDestination,
  buildCustomerDestinationsForOrders,
  buildCustomerHopDestination,
  buildSupplierHopDestination,
  everyOrderHasDestination,
  isHopDestinationLockedToCustomer,
  sharedDestinationHash,
} from "../../../utils/hopDestination";
import { apiService } from "../../../services/ApiService";
import { endpoints } from "../../../utils/endpoints";
import SearchableDestinationSelect from "../../../components/SearchableDestinationSelect";

export interface SelectedRiderDetails {
  id?: string;
  name?: string;
  phone?: string;
  vehicleNumber?: string;
  trackingUrl?: string;
}

export type OrderDestinationsMap = Record<string, HopDestinationPayload>;

interface QuoteBarProps {
  activeGroups: Array<[string, GroupSelection]>;
  selectedOrders: string[];
  quoteCardsExpanded: boolean;
  allowMultiCustomerShipment?: boolean;
  multiHopLogisticsEnabled?: boolean;
  onToggleExpansion: () => void;
  onCloseGroup: (groupKey: string) => void;
  onBookPorter: (
    groupKey: string,
    destinationsByOrder?: OrderDestinationsMap,
  ) => Promise<void>;
  onSelfDispatch: (
    groupKey: string,
    rider?: SelectedRiderDetails,
    destinationsByOrder?: OrderDestinationsMap,
  ) => Promise<void>;
  onSelfPickup: (
    groupKey: string,
    picker?: SelectedRiderDetails
  ) => Promise<void>;
  riders: RiderDetails[] | undefined;
  ridersLoading: boolean;
  selfDispatchButtonLabel?: string;
  /** When true (Dispatch dashboard), Create Shipment skips the rider modal. */
  skipRiderDialog?: boolean;
  /** Book Porter only when allowed by parent (typically R2D). */
  allowBookPorter?: boolean;
}

export default function QuoteBar({
  activeGroups,
  selectedOrders,
  quoteCardsExpanded,
  allowMultiCustomerShipment = false,
  multiHopLogisticsEnabled = false,
  onToggleExpansion,
  onCloseGroup,
  onBookPorter,
  onSelfDispatch,
  onSelfPickup,
  riders,
  ridersLoading,
  selfDispatchButtonLabel = "Self Dispatch",
  skipRiderDialog = false,
  allowBookPorter = false,
}: QuoteBarProps) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const updateGroupQuote = useDispatchDashboardStore((s) => s.updateGroupQuote);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const riderDialog = useDisclosure();
  const destinationDialog = useDisclosure();
  const [pendingGroupKey, setPendingGroupKey] = useState<string>("");
  const [destinationGroupKey, setDestinationGroupKey] = useState<string>("");
  const [destModalKeys, setDestModalKeys] = useState<Record<string, string>>(
    {},
  );
  const [destModalPayloads, setDestModalPayloads] =
    useState<OrderDestinationsMap>({});
  const [destModalResolvingOrder, setDestModalResolvingOrder] = useState<
    string | null
  >(null);
  const [defaultRiders, setDefaultRiders] = useState<
    RiderDetails[] | undefined
  >(riders);
  const [isLoading, setIsLoading] = useState<Boolean>(false);
  const [riderDetails, setRiderDetails] = useState<RiderDetails>({
    id: "",
    name: "",
    phone: "",
    vehicleNumber: "",
    trackingUrl: "",
  });
  /** Ignore stale supplier fetch results while resolving modal destination. */
  const latestDestinationRequestRef = useRef<Record<string, string>>({});
  const [supplierOptions, setSupplierOptions] = useState<any[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const pickerDialog = useDisclosure();
  const [pickerDetails, setPickerDetails] = useState<{
    name: string;
    phone: string;
  }>({ name: "", phone: "" });
  const toast = useToast();
  const [printingGroupKey, setPrintingGroupKey] = useState<string | null>(null);

  /** Multi-hop dest UI only on R2D Self Dispatch. Dispatch Create Shipment uses logisticsPath stamped at R2D. */
  const requirePerOrderDestinations =
    multiHopLogisticsEnabled && !skipRiderDialog;

  useEffect(() => {
    if (riderDialog.isOpen && defaultRiders === undefined && !ridersLoading) {
      (async () => {
        try {
          const res = await getGrazeons({
            userSubType: GARAAZ_PERSONNEL_SUB_TYPES.USTAAD,
          });

          const list: RiderDetails[] = Array.isArray(res?.data)
            ? res.data.map((item: any) => {
                const user = item?.user || {};
                const name = [user.firstName, user.lastName]
                  .filter(Boolean)
                  .join(" ");
                const rawId = user._id ?? item?._id ?? item?.userId;
                return {
                  id: rawId != null ? String(rawId) : "",
                  name,
                  phone: user.mobileNumber || "",
                } as RiderDetails;
              })
            : [];
          setDefaultRiders(list.filter((r) => r.id && r.phone));
        } catch (e) {
          setDefaultRiders([]);
        }
      })();
    }
  }, [riderDialog.isOpen, riders, ridersLoading]);

  const openSelfDispatchDialog = (groupKey: string) => {
    const group = activeGroups.find(([k]) => k === groupKey)?.[1];
    if (group && groupNeedsHopDestination(group)) {
      if (
        !everyOrderHasDestination(
          group.selectedOrders || [],
          group.destinationsByOrder,
        )
      ) {
        toast({
          title: "Destination required",
          description:
            "Select a destination for each order on the quote card before Self Dispatch.",
          status: "warning",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
        return;
      }
    }
    setPendingGroupKey(groupKey);
    setRiderDetails({
      id: "",
      name: "",
      phone: "",
      vehicleNumber: "",
      trackingUrl: "",
    });
    riderDialog.onOpen();
  };

  const handleSelfDispatchClick = async (groupKey: string) => {
    if (skipRiderDialog) {
      // Dispatch dashboard Create Shipment — pass hop destinations on createShipment.
      if (isLoading) return;
      const group = activeGroups.find(([k]) => k === groupKey)?.[1];
      const needsDest = group ? groupNeedsHopDestination(group) : false;
      if (
        needsDest &&
        !everyOrderHasDestination(
          group?.selectedOrders || [],
          group?.destinationsByOrder,
        )
      ) {
        toast({
          title: "Destination required",
          description:
            "Select a destination for each order on the quote card before creating the shipment.",
          status: "warning",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
        return;
      }
      setIsLoading(true);
      try {
        await onSelfDispatch(
          groupKey,
          undefined,
          needsDest ? group?.destinationsByOrder : undefined,
        );
      } finally {
        setIsLoading(false);
      }
      return;
    }
    openSelfDispatchDialog(groupKey);
  };

  const handleBookPorterClick = (groupKey: string) => {
    const group = activeGroups.find(([k]) => k === groupKey)?.[1];
    if (!group || !groupNeedsHopDestination(group)) {
      void onBookPorter(groupKey);
      return;
    }
    const dests = group.destinationsByOrder || {};
    if (
      !everyOrderHasDestination(group.selectedOrders || [], dests)
    ) {
      toast({
        title: "Destination required",
        description:
          "Select a destination for each order on the quote card before booking Porter.",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    const hash = sharedDestinationHash(group.selectedOrders || [], dests);
    if (!hash) {
      toast({
        title: "Same destination required",
        description:
          "Porter can only quote when all selected orders share the same destination.",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    void onBookPorter(groupKey, dests);
  };

  const handleSelectRider = (riderPhone: string) => {
    const rider = defaultRiders?.find((r) => r.phone === riderPhone);
    if (rider) {
      setRiderDetails((prev) => ({
        ...prev,
        id: rider.id ? String(rider.id) : "",
        name: rider.name || "",
        phone: rider.phone || "",
      }));
    }
  };

  const pendingGroup = useMemo(
    () => activeGroups.find(([key]) => key === pendingGroupKey)?.[1],
    [activeGroups, pendingGroupKey],
  );

  const pendingGroupDestinationRows = useMemo(() => {
    if (!pendingGroup?.selectedOrders?.length) return [];

    return pendingGroup.selectedOrders.map((orderNo) => {
      const rawOrderData = pendingGroup.orderDataMap?.[orderNo];
      const customerId =
        rawOrderData?.customerId?.toString?.() ||
        rawOrderData?.customer?._id?.toString?.() ||
        pendingGroup.customerId ||
        "";
      const orderData = {
        ...(rawOrderData || {}),
        customerId,
        customer: {
          ...(rawOrderData?.customer || {}),
          _id: rawOrderData?.customer?._id || customerId,
          name:
            rawOrderData?.customer?.name?.trim() ||
            pendingGroup.customerName ||
            "Customer",
          mobileNumber: rawOrderData?.customer?.mobileNumber?.trim() || "",
        },
      };
      const customerName = orderData.customer.name;
      const addressParts = [
        orderData?.shipmentAddress?.addressLine1,
        orderData?.shipmentAddress?.addressLine2,
        orderData?.shipmentAddress?.city,
        orderData?.shipmentAddress?.state,
        orderData?.shipmentAddress?.pinCode,
      ].filter(Boolean);
      return {
        orderNo,
        customerName,
        addressLabel: addressParts.join(", "),
        orderData,
      };
    });
  }, [pendingGroup]);

  const getGroupDestinationRows = (group: GroupSelection) =>
    group.selectedOrders.map((orderNo) => {
      const rawOrderData = group.orderDataMap?.[orderNo];
      const customerId =
        rawOrderData?.customerId?.toString?.() ||
        rawOrderData?.customer?._id?.toString?.() ||
        group.customerId ||
        "";
      const orderData = {
        ...(rawOrderData || {}),
        customerId,
        customer: {
          ...(rawOrderData?.customer || {}),
          _id: rawOrderData?.customer?._id || customerId,
          name:
            rawOrderData?.customer?.name?.trim() ||
            group.customerName ||
            "Customer",
          mobileNumber: rawOrderData?.customer?.mobileNumber?.trim() || "",
          pickupPoints: rawOrderData?.customer?.pickupPoints,
        },
        logisticsPath: rawOrderData?.logisticsPath,
      };
      return {
        orderNo,
        customerName: orderData.customer.name,
        orderData,
      };
    });

  /** Multi-hop destination only for door-delivery trips (not Self Pickup). */
  const isGroupDoorDelivery = (group: GroupSelection) => {
    const modes = Object.values(
      group.orderDeliveryModes || {},
    ) as DeliveryModes[];
    const unique = Array.from(new Set(modes));
    return (
      unique.length === 1 && unique[0] === DeliveryModes.DOOR_DELIVERY
    );
  };

  const groupNeedsHopDestination = (group: GroupSelection) =>
    requirePerOrderDestinations && isGroupDoorDelivery(group);

  const isGroupDestinationLockedToCustomer = (group: GroupSelection) =>
    groupNeedsHopDestination(group) &&
    areAllOrdersLockedToCustomerDestination(
      group.selectedOrders,
      group.orderDataMap,
      { activeSupplierId: group.supplierId },
    );

  // At customer pickup point: auto-set destination to customer (no modal).
  useEffect(() => {
    if (!requirePerOrderDestinations) return;
    for (const [groupKey, group] of activeGroups) {
      if (!groupNeedsHopDestination(group)) continue;
      if (
        !areAllOrdersLockedToCustomerDestination(
          group.selectedOrders,
          group.orderDataMap,
          { activeSupplierId: group.supplierId },
        )
      ) {
        continue;
      }
      const built = buildCustomerDestinationsForOrders(
        group.selectedOrders,
        group.orderDataMap,
        { customerNameFallback: group.customerName },
      );
      if (!built) continue;

      const alreadySet = everyOrderHasDestination(
        group.selectedOrders,
        group.destinationsByOrder,
      );
      const allCustomer =
        alreadySet &&
        group.selectedOrders.every(
          (orderNo) =>
            group.destinationsByOrder?.[orderNo]?.type === "CUSTOMER",
        );
      if (allCustomer) continue;

      updateGroupQuote(groupKey, {
        destinationsByOrder: built.destinationsByOrder,
        destinationKeysByOrder: built.destinationKeysByOrder,
        porterQuote: null,
        lastQuoteOrderHash: undefined,
        lastQuoteDestinationHash: undefined,
      });
    }
  }, [activeGroups, requirePerOrderDestinations, updateGroupQuote]);

  const openDestinationDialog = (groupKey: string) => {
    const group = activeGroups.find(([k]) => k === groupKey)?.[1];
    if (!group) return;
    if (isGroupDestinationLockedToCustomer(group)) {
      return;
    }
    const rows = getGroupDestinationRows(group);
    const nextKeys: Record<string, string> = {
      ...(group.destinationKeysByOrder || {}),
    };
    const nextPayloads: Record<string, HopDestinationPayload> = {
      ...(group.destinationsByOrder || {}),
    };

    for (const row of rows) {
      const customerPayload = buildCustomerHopDestination(row.orderData || {});
      const customerKey = customerPayload?.customerId
        ? `CUSTOMER:${customerPayload.customerId}`
        : "";
      const locked = isHopDestinationLockedToCustomer(row.orderData || {}, {
        activeSupplierId: group.supplierId,
      });

      if (locked && customerKey && customerPayload) {
        nextKeys[row.orderNo] = customerKey;
        nextPayloads[row.orderNo] = customerPayload;
      } else if (!nextKeys[row.orderNo] && customerKey && customerPayload) {
        // First pass: default destination to customer (still editable).
        nextKeys[row.orderNo] = customerKey;
        nextPayloads[row.orderNo] = customerPayload;
      }
    }

    setDestinationGroupKey(groupKey);
    setDestModalKeys(nextKeys);
    setDestModalPayloads(nextPayloads);
    latestDestinationRequestRef.current = {};
    setDestModalResolvingOrder(null);
    destinationDialog.onOpen();
  };

  const getGroupToLabel = (group: GroupSelection): string => {
    if (!groupNeedsHopDestination(group)) {
      if (allowMultiCustomerShipment && group.isConsolidatedGroup) {
        return `${new Set(Object.values(group.consigneeNames || {})).size} Customers`;
      }
      const modes = Object.values(
        group.orderDeliveryModes || {},
      ) as DeliveryModes[];
      if (
        modes.length > 0 &&
        modes.every((m) => m === DeliveryModes.SELF_PICKUP)
      ) {
        return group.supplierName || "Supplier";
      }
      return group.customerName;
    }
    if (
      !everyOrderHasDestination(
        group.selectedOrders,
        group.destinationsByOrder,
      )
    ) {
      return "Select destination";
    }
    const hash = sharedDestinationHash(
      group.selectedOrders,
      group.destinationsByOrder,
    );
    if (!hash) return "Multiple destinations";
    const dest = group.destinationsByOrder?.[group.selectedOrders[0]];
    if (!dest) return "Select destination";
    if (dest.type === "CUSTOMER") {
      return (
        dest.address?.contact?.name ||
        group.consigneeNames?.[group.selectedOrders[0]] ||
        group.customerName ||
        "Customer"
      );
    }
    return (
      dest.address?.contact?.name ||
      dest.address?.addressLine1 ||
      "Supplier"
    );
  };

  useEffect(() => {
    if (!requirePerOrderDestinations) return;
    let cancelled = false;
    const load = async () => {
      setLoadingSuppliers(true);
      try {
        const response = await apiService
          .axiosCruds()
          .get(endpoints.getSuppliersV2);
        const data = response?.data;
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.suppliers)
            ? data.suppliers
            : [];
        if (!cancelled) setSupplierOptions(list);
      } catch {
        if (!cancelled) setSupplierOptions([]);
      } finally {
        if (!cancelled) setLoadingSuppliers(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [requirePerOrderDestinations]);

  const supplierSelectOptions = useMemo(
    () =>
      supplierOptions.map((s) => ({
        key: `SUPPLIER:${s._id}`,
        label: s.businessName || s.name || String(s._id),
      })),
    [supplierOptions],
  );

  const getOptionsForOrder = (row: {
    orderNo?: string;
    customerName: string;
    orderData: any;
  }) => {
    const options: Array<{ key: string; label: string; isCustomer?: boolean }> =
      [];
    const customerPayload = buildCustomerHopDestination(row.orderData || {});
    if (customerPayload?.customerId) {
      options.push({
        key: `CUSTOMER:${customerPayload.customerId}`,
        label: row.customerName || "Customer",
        isCustomer: true,
      });
    }
    if (
      isHopDestinationLockedToCustomer(row.orderData || {}, {
        activeSupplierId: destinationDialogGroup?.supplierId,
      })
    ) {
      return options;
    }
    return [...options, ...supplierSelectOptions];
  };

  const destinationDialogGroup = useMemo(
    () => activeGroups.find(([k]) => k === destinationGroupKey)?.[1],
    [activeGroups, destinationGroupKey],
  );

  const destinationDialogRows = useMemo(() => {
    if (!destinationDialogGroup) return [];
    return getGroupDestinationRows(destinationDialogGroup);
  }, [destinationDialogGroup]);

  const resolveDestinationPayload = async (
    value: string,
    orderData: any,
  ): Promise<HopDestinationPayload | null> => {
    if (!value) return null;
    if (value.startsWith("CUSTOMER:")) {
      return buildCustomerHopDestination(orderData || {});
    }
    if (value.startsWith("SUPPLIER:")) {
      const supplierId = value.replace("SUPPLIER:", "");
      const response = await apiService
        .axiosCruds()
        .get(endpoints.getSupplierById(supplierId));
      const supplier =
        response?.data ||
        supplierOptions.find((s) => s._id === supplierId) ||
        null;
      return buildSupplierHopDestination(supplier || {});
    }
    return null;
  };

  const handleDestinationModalSelect = async (
    orderNo: string,
    value: string,
    orderData: any,
  ) => {
    if (
      isHopDestinationLockedToCustomer(orderData || {}, {
        activeSupplierId: destinationDialogGroup?.supplierId,
      }) &&
      value &&
      !value.startsWith("CUSTOMER:")
    ) {
      return;
    }
    latestDestinationRequestRef.current[orderNo] = value;
    setDestModalKeys((prev) => ({ ...prev, [orderNo]: value }));
    setDestModalPayloads((prev) => {
      const next = { ...prev };
      delete next[orderNo];
      return next;
    });
    if (!value) return;
    setDestModalResolvingOrder(orderNo);
    try {
      const payload = await resolveDestinationPayload(value, orderData);
      if (latestDestinationRequestRef.current[orderNo] !== value) return;
      if (!payload?.address?.contact?.phoneNumber?.trim() && payload) {
        toast({
          title: "Destination phone missing",
          description:
            "Update phone for the selected destination, then try again.",
          status: "warning",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
        return;
      }
      if (!payload) {
        toast({
          title: "Destination incomplete",
          description:
            "Selected destination is missing address or phone details.",
          status: "warning",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
        return;
      }
      setDestModalPayloads((prev) => ({ ...prev, [orderNo]: payload }));
    } catch {
      if (latestDestinationRequestRef.current[orderNo] !== value) return;
      toast({
        title: "Failed to load destination",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } finally {
      if (latestDestinationRequestRef.current[orderNo] === value) {
        setDestModalResolvingOrder(null);
      }
    }
  };

  const allModalDestinationsReady = useMemo(() => {
    if (!destinationDialogRows.length) return false;
    return destinationDialogRows.every(
      (row) => !!destModalPayloads[row.orderNo],
    );
  }, [destinationDialogRows, destModalPayloads]);

  const handleConfirmDestinationDialog = () => {
    if (!destinationGroupKey || !allModalDestinationsReady) {
      toast({
        title: "Destination required",
        description: "Select a destination for every order before continuing.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    updateGroupQuote(destinationGroupKey, {
      destinationKeysByOrder: { ...destModalKeys },
      destinationsByOrder: { ...destModalPayloads },
      porterQuote: null,
      lastQuoteOrderHash: undefined,
      lastQuoteDestinationHash: undefined,
    });
    destinationDialog.onClose();
  };

  const allOrdersHaveDestination = useMemo(() => {
    if (!requirePerOrderDestinations) return true;
    if (!pendingGroup) return false;
    return everyOrderHasDestination(
      pendingGroup.selectedOrders,
      pendingGroup.destinationsByOrder,
    );
  }, [requirePerOrderDestinations, pendingGroup]);

  const handleConfirmSelfDispatch = async () => {
    if (isLoading) return;

    if (requirePerOrderDestinations && !allOrdersHaveDestination) {
      toast({
        title: "Destination required",
        description: "Select a destination for each order before continuing",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    if (requirePerOrderDestinations) {
      const missingPhone = pendingGroupDestinationRows.find((row) => {
        const dest = pendingGroup?.destinationsByOrder?.[row.orderNo];
        return !dest?.address?.contact?.phoneNumber?.trim();
      });
      if (missingPhone) {
        toast({
          title: "Destination contact phone missing",
          description: `Update phone for the selected destination on order ${missingPhone.orderNo} (customer or supplier), then try again.`,
          status: "error",
          duration: 6000,
          isClosable: true,
          position: "top",
        });
        return;
      }
    }

    if (
      !skipRiderDialog &&
      riderDetails.trackingUrl?.trim() &&
      riderDetails.trackingUrl.trim().includes(" ")
    ) {
      return;
    }

    const riderId = String(riderDetails.id || "").trim();

    // R2D Self Dispatch: rider is mandatory — do not move to DISPATCHED without one.
    if (!skipRiderDialog && !riderId) {
      toast({
        title: "Rider must be selected",
        description:
          "Select a saved rider from the list before Self Dispatch.",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setIsLoading(true);
    const rider =
      !skipRiderDialog &&
      (riderId ||
        riderDetails.name ||
        riderDetails.phone ||
        riderDetails.vehicleNumber ||
        riderDetails.trackingUrl)
        ? {
            id: riderId || undefined,
            name: riderDetails.name || undefined,
            phone: riderDetails.phone || undefined,
            vehicleNumber: riderDetails.vehicleNumber || undefined,
            trackingUrl: riderDetails.trackingUrl?.trim() || undefined,
          }
        : undefined;

    if (
      rider &&
      !rider.id &&
      (rider.name || rider.phone || rider.vehicleNumber || rider.trackingUrl)
    ) {
      toast({
        title: "Select a saved rider",
        description:
          "Choose a rider from the saved list so the order can be allocated. Name/phone alone is not enough.",
        status: "error",
        duration: 6000,
        isClosable: true,
        position: "top",
      });
      setIsLoading(false);
      return;
    }

    try {
      await onSelfDispatch(
        pendingGroupKey,
        rider,
        requirePerOrderDestinations
          ? pendingGroup?.destinationsByOrder
          : undefined,
      );
      riderDialog.onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const openSelfPickupDialog = (groupKey: string) => {
    setPendingGroupKey(groupKey);
    setPickerDetails({ name: "", phone: "" });
    pickerDialog.onOpen();
  };

  const handleConfirmSelfPickup = async () => {
    if (isLoading) return;

    if (!pickerDetails.name.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter the picker's name",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    if (pickerDetails.name.trim().length > 40) {
      toast({
        title: "Name Too Long",
        description: "Name must be 40 characters or less",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    if (!pickerDetails.phone.trim()) {
      toast({
        title: "Phone Number Required",
        description: "Please enter the picker's phone number",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(pickerDetails.phone.replace(/\D/g, ""))) {
      toast({
        title: "Invalid Phone Number",
        description: "Please enter a valid phone number (exactly 10 digits)",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setIsLoading(true);
    try {
      await onSelfPickup(pendingGroupKey, pickerDetails);
      pickerDialog.onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintGroupInvoices = async (
    groupKey: string,
    invoiceMediaIds: InvoiceMediaIdentifier[]
  ) => {
    setPrintingGroupKey(groupKey);
    try {
      const result = await printGaraazInvoices(invoiceMediaIds);
      showInvoicePrintSuccess(result.printableCount, toast);
    } catch (error) {
      handleInvoicePrintError(error, toast, {
        title: "Unable to print invoices",
      });
    } finally {
      setPrintingGroupKey(null);
    }
  };

  const checkScrollPosition = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const checkScrollPositionDelayed = () => {
    setTimeout(checkScrollPosition, 50);
  };
  useEffect(() => {
    if (quoteCardsExpanded) {
      checkScrollPositionDelayed();
    } else {
      checkScrollPosition();
    }

    const container = scrollContainerRef.current;
    if (container) {
      const resizeObserver = new ResizeObserver(() => {
        checkScrollPosition();
      });
      resizeObserver.observe(container);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [activeGroups, quoteCardsExpanded]);

  if (activeGroups.length === 0) {
    return null;
  }

  const getDeliveryModeButtons = (group: GroupSelection, groupKey: string) => {
    const isConsolidated =
      allowMultiCustomerShipment &&
      (group.isConsolidatedGroup || groupKey.startsWith("supplier__"));

    if (isConsolidated) {
      return {
        showBookPorter: false,
        showSelfDispatch: true,
        showSelfPickup: false,
        showMultipleModes: false,
      };
    }

    const deliveryModes = Object.values(
      group.orderDeliveryModes || {}
    ) as DeliveryModes[];
    const uniqueDeliveryModes = Array.from(new Set(deliveryModes));

    if (uniqueDeliveryModes.length === 0) {
      return {
        showBookPorter: false,
        showSelfDispatch: false,
        showSelfPickup: false,
        showMultipleModes: false,
      };
    }

    if (uniqueDeliveryModes.length === 1) {
      const mode = uniqueDeliveryModes[0];
      if (mode === DeliveryModes.SELF_PICKUP) {
        return {
          showBookPorter: false,
          showSelfDispatch: false,
          showSelfPickup: true,
          showMultipleModes: false,
        };
      } else if (mode === DeliveryModes.DOOR_DELIVERY) {
        return {
          showBookPorter: allowBookPorter,
          showSelfDispatch: true,
          showSelfPickup: false,
          showMultipleModes: false,
        };
      }
    }

    return {
      showBookPorter: false,
      showSelfDispatch: false,
      showSelfPickup: false,
      showMultipleModes: true,
    };
  };

  return (
    <>
      <Box
        bg="white"
        borderRadius="xl"
        shadow="0 4px 20px rgba(0, 0, 0, 0.08)"
        border="1px solid"
        borderColor="gray.100"
        position="sticky"
        top="0"
        zIndex="10"
        mb={6}
      >
        {/* Header with Summary and Toggle */}
        <Box
          p={2}
          borderBottom={quoteCardsExpanded ? "1px solid" : "none"}
          borderColor="gray.100"
          cursor="pointer"
          onClick={onToggleExpansion}
          _hover={{ bg: "gray.50" }}
          transition="background-color 0.2s"
        >
          <HStack justify="space-between" align="center">
            <HStack spacing={3}>
              <Badge
                bg="blue.500"
                color="white"
                variant="subtle"
                px={3}
                py={2}
                borderRadius="full"
                fontWeight="600"
                border="1px"
                borderColor="gray.200"
                lineHeight={"normal"}
                textTransform={"none"}
                fontSize="sm"
              >
                {activeGroups.length} Quote
                {activeGroups.length !== 1 ? "s" : ""}
              </Badge>

              <Badge
                bg="white"
                color="gray.700"
                variant="subtle"
                px={3}
                py={2}
                borderRadius="full"
                fontWeight="600"
                border="1px"
                borderColor="gray.200"
                lineHeight={"normal"}
                textTransform={"none"}
                fontSize="sm"
              >
                {selectedOrders.length} Orders
              </Badge>
            </HStack>

            <HStack spacing={7}>
              <IconButton
                aria-label="Toggle expansion"
                icon={
                  quoteCardsExpanded ? (
                    <ChevronUpIcon boxSize={6} />
                  ) : (
                    <ChevronDownIcon boxSize={6} />
                  )
                }
                variant="ghost"
                size="lg"
                _hover={{
                  bg: "none",
                }}
                px={7}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpansion();
                }}
              />
            </HStack>
          </HStack>
        </Box>

        {/* Collapsible Cards Content */}
        <Collapse in={quoteCardsExpanded}>
          <Box p={4} position="relative">
            {/* Enhanced Scroll Buttons */}
            {activeGroups.length > 3 && (
              <>
                {/* Left Scroll Button - Hidden when at start */}
                {canScrollLeft && (
                  <Box
                    position="absolute"
                    left={0}
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex={12}
                    paddingLeft={1}
                    paddingRight={1}
                    paddingTop={3}
                    paddingBottom={3}
                    bg="white"
                    borderTopRightRadius="xl"
                    borderBottomRightRadius="xl"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    transition="all 0.3s ease"
                    boxShadow="0 0px 5px #667eea"
                    cursor="pointer"
                    onClick={() => {
                      const container = scrollContainerRef.current;
                      if (container) {
                        container.scrollBy({ left: -400, behavior: "smooth" });
                      }
                    }}
                    _hover={{
                      boxShadow: "0 0px 12px #667eea",
                    }}
                  >
                    <Icon as={ChevronLeftIcon} boxSize={6} color="gray.600" />
                  </Box>
                )}

                {/* Right Scroll Button - Hidden when at end */}
                {canScrollRight && (
                  <Box
                    position="absolute"
                    right={0}
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex={12}
                    paddingLeft={1}
                    paddingRight={1}
                    paddingTop={3}
                    paddingBottom={3}
                    bg="white"
                    borderTopLeftRadius="xl"
                    borderBottomLeftRadius="xl"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    transition="all 0.3s ease"
                    boxShadow="0 0px 5px #667eea"
                    cursor="pointer"
                    onClick={() => {
                      const container = scrollContainerRef.current;
                      if (container) {
                        container.scrollBy({ left: 400, behavior: "smooth" });
                      }
                    }}
                    _hover={{
                      boxShadow: "0 0px 12px #667eea",
                    }}
                  >
                    <Icon as={ChevronRightIcon} boxSize={6} color="gray.600" />
                  </Box>
                )}
              </>
            )}

            <Box
              ref={scrollContainerRef}
              position="relative"
              overflowX="auto"
              overflowY="hidden"
              scrollBehavior="smooth"
              onScroll={checkScrollPosition}
              sx={{
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <HStack spacing={4} align="stretch" minW="max-content" pb={2}>
                {activeGroups.map(([groupKey, group]) => {
                  const invoiceMediaIds = extractInvoiceMediaIdsFromGroup(
                    group.selectedOrders,
                    group.orderDataMap
                  );
                  const hasPrintableInvoices = invoiceMediaIds.some((value) =>
                    Boolean(normalizeInvoiceMediaId(value))
                  );

                  return (
                    <Box
                      key={groupKey}
                      bg="white"
                      borderRadius="lg"
                      p={3}
                      shadow="0 2px 12px rgba(0, 0, 0, 0.06)"
                      border="1px solid"
                      borderColor="gray.200"
                      minW="350px"
                      maxW="350px"
                      position="relative"
                      _hover={{
                        shadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
                        transform: "translateY(-2px)",
                      }}
                      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                    >
                      {/* Printer icon at top left */}
                      <HStack
                        position="absolute"
                        top={2}
                        left={2}
                        spacing={1}
                        zIndex={11}
                      >
                        <Tooltip
                          label={
                            hasPrintableInvoices
                              ? "Print Garaaz invoices"
                              : "No Garaaz invoices available yet"
                          }
                        >
                          <IconButton
                            aria-label="Print Garaaz invoices"
                            icon={<FaPrint />}
                            size="sm"
                            variant="ghost"
                            color="gray.500"
                            isDisabled={
                              !hasPrintableInvoices ||
                              printingGroupKey === groupKey
                            }
                            isLoading={printingGroupKey === groupKey}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrintGroupInvoices(
                                groupKey,
                                invoiceMediaIds
                              );
                            }}
                            _hover={{
                              color: "purple.600",
                              bg: "purple.50",
                            }}
                          />
                        </Tooltip>
                      </HStack>
                      <HStack
                        position="absolute"
                        top={2}
                        right={2}
                        spacing={1}
                        zIndex={11}
                      >
                        <IconButton
                          aria-label="Cancel quote card"
                          icon={<CloseIcon boxSize={2.5} />}
                          size="sm"
                          variant="ghost"
                          onClick={() => onCloseGroup(groupKey)}
                        />
                      </HStack>

                      {/* Card Content */}
                      <VStack spacing={2} align="stretch">
                        {/* Route Info - Compact and Centered */}
                        <VStack spacing={0.5} align="center">
                          <HStack
                            spacing={0}
                            align="center"
                            justify="center"
                            w="100%"
                          >
                            <VStack align="center" flex="1" minW="0">
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                fontWeight="500"
                                textTransform="uppercase"
                              >
                                From
                              </Text>
                              <Tooltip
                                label={group.supplierName}
                                placement="top"
                                hasArrow
                              >
                                <Text
                                  fontSize="sm"
                                  color="gray.700"
                                  fontWeight="600"
                                  maxW="100px"
                                  noOfLines={1}
                                  cursor="pointer"
                                >
                                  {group.supplierName}
                                </Text>
                              </Tooltip>
                            </VStack>
                            <VStack align="center" spacing={0} px={0}>
                              <Icon
                                as={FaArrowRight}
                                boxSize={3}
                                color="blue.500"
                              />
                            </VStack>
                            <VStack align="center" flex="1" minW="0">
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                fontWeight="500"
                                textTransform="uppercase"
                              >
                                To
                              </Text>
                              <Tooltip
                                label={
                                  allowMultiCustomerShipment &&
                                  group.isConsolidatedGroup
                                    ? Object.values(
                                        group.consigneeNames || {}
                                      ).join(", ")
                                    : getGroupToLabel(group)
                                }
                                placement="top"
                                hasArrow
                              >
                                <Text
                                  fontSize="sm"
                                  color="gray.700"
                                  fontWeight="600"
                                  maxW="100px"
                                  noOfLines={1}
                                  cursor="pointer"
                                >
                                  {getGroupToLabel(group)}
                                </Text>
                              </Tooltip>
                            </VStack>
                          </HStack>
                        </VStack>

                        {groupNeedsHopDestination(group) && (
                          <Box px={1}>
                            {isGroupDestinationLockedToCustomer(group) ? (
                              <>
                                <Button
                                  size="sm"
                                  w="100%"
                                  variant="outline"
                                  colorScheme="teal"
                                  isDisabled
                                  cursor="not-allowed"
                                >
                                  {`To customer · ${getGroupToLabel(group)}`}
                                </Button>
                                <Text
                                  fontSize="xs"
                                  color="gray.500"
                                  mt={1}
                                  textAlign="center"
                                >
                                  At pickup point — destination is the customer
                                </Text>
                              </>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  w="100%"
                                  variant="outline"
                                  colorScheme={
                                    everyOrderHasDestination(
                                      group.selectedOrders,
                                      group.destinationsByOrder,
                                    )
                                      ? "teal"
                                      : "blue"
                                  }
                                  onClick={() =>
                                    openDestinationDialog(groupKey)
                                  }
                                >
                                  {everyOrderHasDestination(
                                    group.selectedOrders,
                                    group.destinationsByOrder,
                                  )
                                    ? `Change destination · ${getGroupToLabel(group)}`
                                    : "Select destination"}
                                </Button>
                                {!everyOrderHasDestination(
                                  group.selectedOrders,
                                  group.destinationsByOrder,
                                ) && (
                                  <Text
                                    fontSize="xs"
                                    color="gray.500"
                                    mt={1}
                                    textAlign="center"
                                  >
                                    {group.selectedOrders.length > 1
                                      ? `Select destination for each of ${group.selectedOrders.length} orders`
                                      : "Required for Porter fare and ETA"}
                                  </Text>
                                )}
                              </>
                            )}
                          </Box>
                        )}

                        {/* Quote Info Grid - Compact with Vertical Lines */}
                        {groupNeedsHopDestination(group) &&
                        !everyOrderHasDestination(
                          group.selectedOrders,
                          group.destinationsByOrder,
                        ) ? (
                          <Box
                            bg="gray.50"
                            borderRadius="md"
                            p={2}
                            textAlign="center"
                          >
                            <Text fontSize="xs" color="gray.600" fontWeight="500">
                              Waiting for destination…
                            </Text>
                          </Box>
                        ) : group.porterQuoteLoading ? (
                          <Box
                            bg="blue.50"
                            borderRadius="md"
                            p={1.5}
                            textAlign="center"
                          >
                            <HStack justify="center" spacing={2}>
                              <Spinner size="sm" color="blue.500" />
                              <Text
                                fontSize="xs"
                                color="blue.700"
                                fontWeight="500"
                              >
                                Getting quotes...
                              </Text>
                            </HStack>
                          </Box>
                        ) : Array.from(
                            new Set(
                              Object.values(
                                group.orderDeliveryModes || {}
                              ) as DeliveryModes[]
                            )
                          ).length === 1 &&
                          (
                            Object.values(
                              group.orderDeliveryModes || {}
                            ) as DeliveryModes[]
                          )[0] === DeliveryModes.DOOR_DELIVERY &&
                          group.porterQuote?.vehicles &&
                          group.porterQuote.vehicles.length > 0 ? (
                          <HStack
                            justify="space-between"
                            align="center"
                            spacing={0}
                            borderRadius="md"
                            px={5}
                            py={2}
                          >
                            <VStack align="center" spacing={0.5}>
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                fontWeight="500"
                                textTransform="uppercase"
                              >
                                Orders
                              </Text>
                              <Text
                                fontSize="md"
                                fontWeight="bold"
                                color="blue.600"
                              >
                                {group.selectedOrders.length}
                              </Text>
                            </VStack>

                            <Box w="1px" h="6" bg="gray.300" />

                            <VStack align="center" spacing={0.5}>
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                fontWeight="500"
                                textTransform="uppercase"
                              >
                                EST. Fare
                              </Text>
                              <Text
                                fontSize="md"
                                fontWeight="bold"
                                color="green.600"
                              >
                                ₹
                                {(() => {
                                  const fares = group.porterQuote?.vehicles
                                    ?.map(
                                      (vehicle: any) =>
                                        vehicle?.fare?.minor_amount
                                    )
                                    ?.filter(
                                      (fare: any) =>
                                        fare !== undefined && fare !== null
                                    );
                                  if (!fares || fares.length === 0) return 0;
                                  const minFare = Math.min(...fares);
                                  return minFare / 100;
                                })()}
                              </Text>
                            </VStack>

                            <Box w="1px" h="6" bg="gray.300" />

                            <VStack align="center" spacing={0.5}>
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                fontWeight="500"
                                textTransform="uppercase"
                              >
                                ETA
                              </Text>
                              <Text
                                fontSize="md"
                                fontWeight="bold"
                                color="orange.600"
                              >
                                {(() => {
                                  const etas = group.porterQuote?.vehicles
                                    ?.map((vehicle: any) => vehicle?.eta?.value)
                                    ?.filter(
                                      (eta: any): eta is number =>
                                        eta !== undefined && eta !== null
                                    );
                                  if (!etas || etas.length === 0) return "N/A";
                                  const minEta = Math.min(...etas);
                                  const firstVehicle =
                                    group.porterQuote!.vehicles[0];
                                  const unit =
                                    (firstVehicle as any)?.eta?.unit || "mins";
                                  return `${minEta} ${unit}`;
                                })()}
                              </Text>
                            </VStack>
                          </HStack>
                        ) : (
                          (() => {
                            const deliveryModes = Object.values(
                              group.orderDeliveryModes || {}
                            ) as DeliveryModes[];
                            const uniqueDeliveryModes = Array.from(
                              new Set(deliveryModes)
                            );

                            if (
                              uniqueDeliveryModes.length === 1 &&
                              uniqueDeliveryModes[0] ===
                                DeliveryModes.DOOR_DELIVERY
                            ) {
                              return (
                                <Box
                                  bg="yellow.50"
                                  borderRadius="lg"
                                  px={2}
                                  py={1.5}
                                  border="1px solid"
                                  borderColor="yellow.200"
                                  minH="64px"
                                  display="flex"
                                  alignItems="center"
                                  w="100%"
                                  borderLeftWidth="4px"
                                  borderLeftColor="yellow.400"
                                >
                                  <HStack justify="center" spacing={3} w="100%">
                                    <HStack spacing={3}>
                                      <VStack spacing={0} align="center">
                                        <HStack spacing={2}>
                                          <Icon
                                            as={IoMdPricetag}
                                            boxSize={4}
                                            color="yellow.700"
                                          />
                                          <Text
                                            fontSize="sm"
                                            color="yellow.900"
                                            fontWeight="700"
                                          >
                                            Quote pending
                                          </Text>
                                          <Badge
                                            colorScheme="yellow"
                                            variant="subtle"
                                          >
                                            Door Delivery
                                          </Badge>
                                        </HStack>
                                        <Text
                                          fontSize="xs"
                                          color="yellow.800"
                                          fontWeight="500"
                                        >
                                          Waiting for fares & ETA
                                        </Text>
                                      </VStack>
                                    </HStack>
                                  </HStack>
                                </Box>
                              );
                            }

                            if (
                              uniqueDeliveryModes.length === 1 &&
                              uniqueDeliveryModes[0] ===
                                DeliveryModes.SELF_PICKUP
                            ) {
                              return (
                                <Box
                                  bg="green.50"
                                  borderRadius="lg"
                                  px={2}
                                  py={1.5}
                                  border="1px solid"
                                  borderColor="green.200"
                                  minH="64px"
                                  display="flex"
                                  alignItems="center"
                                  w="100%"
                                  borderLeftWidth="4px"
                                  borderLeftColor="green.400"
                                >
                                  <HStack justify="center" spacing={3} w="100%">
                                    <HStack spacing={3} align="center">
                                      <Icon
                                        as={FaWalking}
                                        boxSize={4}
                                        color="green.700"
                                      />
                                      <VStack spacing={0} align="start">
                                        <HStack spacing={2}>
                                          <Text
                                            fontSize="sm"
                                            color="green.900"
                                            fontWeight="700"
                                          >
                                            No quote needed
                                          </Text>
                                          <Badge
                                            colorScheme="green"
                                            variant="subtle"
                                          >
                                            Self Pickup
                                          </Badge>
                                        </HStack>
                                        <Text
                                          fontSize="xs"
                                          color="green.800"
                                          fontWeight="500"
                                        >
                                          Proceed with Self Pickup flow
                                        </Text>
                                      </VStack>
                                    </HStack>
                                  </HStack>
                                </Box>
                              );
                            }

                            return (
                              <Box
                                bg="blue.50"
                                borderRadius="lg"
                                px={2}
                                py={1.5}
                                border="1px solid"
                                borderColor="blue.200"
                                minH="105px"
                                display="flex"
                                alignItems="center"
                                w="100%"
                                borderLeftWidth="4px"
                                borderLeftColor="blue.400"
                              >
                                <HStack justify="center" spacing={3} w="100%">
                                  <HStack spacing={3}>
                                    <VStack spacing={0} align="center">
                                      <HStack spacing={2}>
                                        <Icon
                                          as={FaExclamationTriangle}
                                          boxSize={4}
                                          color="blue.700"
                                        />
                                        <Text
                                          fontSize="sm"
                                          color="blue.900"
                                          fontWeight="700"
                                        >
                                          Multiple Delivery Modes Detected
                                        </Text>
                                      </HStack>
                                      <Text
                                        fontSize="xs"
                                        color="blue.800"
                                        fontWeight="500"
                                      >
                                        Unify delivery modes to enable actions
                                      </Text>
                                    </VStack>
                                  </HStack>
                                </HStack>
                              </Box>
                            );
                          })()
                        )}
                        <HStack spacing={2} align="stretch">
                          {(() => {
                            const buttons = getDeliveryModeButtons(group, groupKey);
                            const buttonElements: React.ReactNode[] = [];

                            if (buttons.showMultipleModes) {
                            } else {
                              if (buttons.showBookPorter) {
                                buttonElements.push(
                                  <Button
                                    key="book-porter"
                                    onClick={() =>
                                      handleBookPorterClick(groupKey)
                                    }
                                    bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                                    color="white"
                                    size="sm"
                                    fontSize="xs"
                                    fontWeight="600"
                                    borderRadius="md"
                                    leftIcon={<FaTruck />}
                                    isLoading={group.porterQuoteLoading}
                                    loadingText="Getting Quote..."
                                    disabled={
                                      group.porterQuoteLoading ||
                                      (groupNeedsHopDestination(group) &&
                                        !sharedDestinationHash(
                                          group.selectedOrders,
                                          group.destinationsByOrder,
                                        ))
                                    }
                                    flex={1}
                                    _hover={{
                                      transform: "translateY(-1px)",
                                      boxShadow:
                                        "0 4px 15px rgba(102, 126, 234, 0.4)",
                                      bg: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                                    }}
                                    _active={{
                                      transform: "translateY(0px)",
                                    }}
                                    _disabled={{
                                      opacity: 0.6,
                                      cursor: "not-allowed",
                                    }}
                                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                  >
                                    Book Porter
                                  </Button>
                                );
                              }

                              if (buttons.showSelfDispatch) {
                                buttonElements.push(
                                  <Button
                                    key="self-dispatch"
                                    onClick={() =>
                                      handleSelfDispatchClick(groupKey)
                                    }
                                    bg="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                                    color="white"
                                    size="sm"
                                    fontSize="xs"
                                    fontWeight="600"
                                    borderRadius="md"
                                    leftIcon={<FaTruck />}
                                    flex={1}
                                    isLoading={!!isLoading && skipRiderDialog}
                                    isDisabled={!!isLoading}
                                    _hover={{
                                      transform: "translateY(-1px)",
                                      boxShadow:
                                        "0 4px 15px rgba(240, 147, 251, 0.4)",
                                      bg: "linear-gradient(135deg, #e085f0 0%, #f04a5c 100%)",
                                    }}
                                    _active={{
                                      transform: "translateY(0px)",
                                    }}
                                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                  >
                                    {selfDispatchButtonLabel}
                                  </Button>
                                );
                              }

                              if (buttons.showSelfPickup) {
                                buttonElements.push(
                                  <Button
                                    key="self-pickup"
                                    onClick={() =>
                                      openSelfPickupDialog(groupKey)
                                    }
                                    bg="linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%)"
                                    color="white"
                                    size="sm"
                                    fontSize="xs"
                                    fontWeight="600"
                                    borderRadius="md"
                                    leftIcon={<FaWalking />}
                                    flex={1}
                                    _hover={{
                                      transform: "translateY(-1px)",
                                      boxShadow:
                                        "0 4px 15px rgba(86, 171, 47, 0.4)",
                                      bg: "linear-gradient(135deg, #4a9a2a 0%, #96d4b8 100%)",
                                    }}
                                    _active={{
                                      transform: "translateY(0px)",
                                    }}
                                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                  >
                                    Self Pickup
                                  </Button>
                                );
                              }
                            }

                            return buttonElements;
                          })()}
                        </HStack>
                      </VStack>
                    </Box>
                  );
                })}
              </HStack>
            </Box>
          </Box>
        </Collapse>
      </Box>

      <Modal
        isOpen={destinationDialog.isOpen}
        onClose={destinationDialog.onClose}
        isCentered
        size="md"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxH="85vh">
          <ModalHeader>Select destination</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {destinationDialogGroup && (
                <Text fontSize="sm" color="gray.600">
                  From{" "}
                  <Text as="span" fontWeight="600" color="gray.800">
                    {destinationDialogGroup.supplierName}
                  </Text>
                  {destinationDialogGroup.selectedOrders.length > 1
                    ? ` · choose destination for each of ${destinationDialogGroup.selectedOrders.length} orders`
                    : ` · order ${destinationDialogGroup.selectedOrders[0]}`}
                </Text>
              )}
              {destinationDialogRows.length === 0 ? (
                <Text fontSize="sm" color="gray.500">
                  No orders in this selection.
                </Text>
              ) : (
                <VStack spacing={3} align="stretch">
                  {destinationDialogRows.map((row) => {
                    const payload = destModalPayloads[row.orderNo];
                    const isResolving =
                      destModalResolvingOrder === row.orderNo;
                    const isLocked = isHopDestinationLockedToCustomer(
                      row.orderData || {},
                      {
                        activeSupplierId: destinationDialogGroup?.supplierId,
                      },
                    );
                    return (
                      <FormControl key={row.orderNo} isRequired>
                        <FormLabel mb={1}>
                          <HStack spacing={2} align="baseline">
                            <Text fontSize="sm" fontWeight="600">
                              {row.orderNo}
                            </Text>
                            <Text fontSize="xs" color="gray.500" fontWeight="400">
                              {row.customerName}
                            </Text>
                          </HStack>
                        </FormLabel>
                        <SearchableDestinationSelect
                          valueKey={destModalKeys[row.orderNo] || ""}
                          displayLabel={
                            payload?.type === "SUPPLIER"
                              ? payload.address.contact?.name ||
                                payload.address.addressLine1 ||
                                "Supplier"
                              : payload?.type === "CUSTOMER"
                                ? row.customerName
                                : undefined
                          }
                          options={getOptionsForOrder(row)}
                          isLoading={loadingSuppliers || isResolving}
                          isDisabled={isLocked}
                          placeholder="Select destination of this order"
                          onChange={(key) =>
                            void handleDestinationModalSelect(
                              row.orderNo,
                              key,
                              row.orderData,
                            )
                          }
                        />
                        {isLocked && (
                          <FormHelperText mt={1}>
                            At customer pickup point — destination is the
                            customer.
                          </FormHelperText>
                        )}
                      </FormControl>
                    );
                  })}
                </VStack>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={destinationDialog.onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleConfirmDestinationDialog}
                isDisabled={
                  !allModalDestinationsReady || !!destModalResolvingOrder
                }
                isLoading={!!destModalResolvingOrder}
              >
                Confirm destinations
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={riderDialog.isOpen}
        onClose={riderDialog.onClose}
        isCentered
        size="3xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Self Dispatch</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack
              align="stretch"
              spacing={6}
              flexDir={{ base: "column", md: "row" }}
            >
              <VStack spacing={4} align="stretch" flex={1} minW={0}>
                <Text fontSize="sm" fontWeight="700" color="gray.700">
                  Rider details
                </Text>
                <FormControl>
                  <FormLabel>Select saved rider</FormLabel>
                  <Select
                    placeholder="Select rider"
                    onChange={(e) => handleSelectRider(e.target.value)}
                  >
                    {defaultRiders?.map((r) => (
                      <option
                        key={r.phone}
                        value={r.phone}
                      >{`${r.name} (${r.phone})`}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Rider name</FormLabel>
                  <Input
                    value={riderDetails.name}
                    isReadOnly
                    isDisabled
                    placeholder="Select rider to populate"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Rider phone</FormLabel>
                  <Input
                    value={riderDetails.phone}
                    isReadOnly
                    isDisabled
                    placeholder="Select rider to populate"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Vehicle number</FormLabel>
                  <Input
                    value={riderDetails.vehicleNumber}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRiderDetails((prev) => ({
                        ...prev,
                        vehicleNumber: v,
                      }));
                    }}
                    placeholder="e.g. AB-12-AB-1234"
                  />
                </FormControl>
                <FormControl
                  isInvalid={riderDetails.trackingUrl?.trim().includes(" ")}
                >
                  <FormLabel>Tracking URL</FormLabel>
                  <Input
                    value={riderDetails.trackingUrl}
                    onChange={(e) => {
                      const url = e.target.value;
                      setRiderDetails((prev) => ({
                        ...prev,
                        trackingUrl: url,
                      }));
                    }}
                    placeholder="Enter tracking URL"
                    maxLength={500}
                  />
                  {riderDetails.trackingUrl?.trim().includes(" ") && (
                    <FormHelperText color="red.500">
                      Tracking URL cannot contain spaces
                    </FormHelperText>
                  )}
                </FormControl>
              </VStack>

              <Box
                w={{ base: "100%", md: "1px" }}
                h={{ base: "1px", md: "auto" }}
                bg="gray.200"
                alignSelf="stretch"
              />

              <VStack
                spacing={4}
                align="stretch"
                flex={1}
                minW={0}
                maxH={{ base: "none", md: "420px" }}
                overflowY="auto"
              >
                <Text fontSize="sm" fontWeight="700" color="gray.700">
                  {requirePerOrderDestinations
                    ? "Selected destination"
                    : "Orders"}
                </Text>
                {pendingGroupDestinationRows.length > 0 ? (
                  pendingGroupDestinationRows.map((row) => {
                    const selectedPayload =
                      pendingGroup?.destinationsByOrder?.[row.orderNo];
                    const destinationLabel =
                      selectedPayload?.type === "SUPPLIER"
                        ? selectedPayload.address.contact?.name ||
                          selectedPayload.address.addressLine1 ||
                          "Supplier"
                        : row.customerName || "Customer";
                    const addressParts = [
                      selectedPayload?.address?.addressLine1,
                      selectedPayload?.address?.addressLine2,
                      selectedPayload?.address?.city,
                      selectedPayload?.address?.state,
                      selectedPayload?.address?.pinCode,
                    ].filter(Boolean);
                    return (
                      <Box
                        key={row.orderNo}
                        borderWidth="1px"
                        borderColor="gray.200"
                        borderRadius="md"
                        p={3}
                        bg="gray.50"
                      >
                        <Text fontSize="sm" color="gray.800" mb={1}>
                          <Text as="span" fontWeight="600">
                            Order number:
                          </Text>{" "}
                          {row.orderNo}
                        </Text>
                        <Text fontSize="sm" color="gray.700" fontWeight="600">
                          {destinationLabel}
                        </Text>
                        {addressParts.length > 0 && (
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            {addressParts.join(", ")}
                          </Text>
                        )}
                      </Box>
                    );
                  })
                ) : (
                  <Text fontSize="sm" color="gray.500">
                    No orders selected
                  </Text>
                )}
              </VStack>
            </HStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={riderDialog.onClose}>
                Cancel
              </Button>
              <Button
                bg={"blue.500"}
                onClick={handleConfirmSelfDispatch}
                isLoading={!!isLoading}
                isDisabled={
                  !!isLoading ||
                  riderDetails.trackingUrl?.trim().includes(" ") ||
                  (requirePerOrderDestinations && !allOrdersHaveDestination) ||
                  (!skipRiderDialog && !String(riderDetails.id || "").trim())
                }
                loadingText="Dispatching..."
              >
                Continue
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={pickerDialog.isOpen}
        onClose={pickerDialog.onClose}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Picker Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Picker Name</FormLabel>
                <Input
                  value={pickerDetails.name}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPickerDetails((prev) => ({ ...prev, name: v }));
                  }}
                  placeholder="Enter picker name"
                  isInvalid={pickerDetails.name.trim() === ""}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Phone Number</FormLabel>
                <Input
                  value={pickerDetails.phone}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPickerDetails((prev) => ({ ...prev, phone: v }));
                  }}
                  placeholder="Enter phone number"
                  type="tel"
                  isInvalid={pickerDetails.phone.trim() === ""}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={pickerDialog.onClose}>
                Cancel
              </Button>
              <Button
                bg={"blue.500"}
                onClick={handleConfirmSelfPickup}
                isLoading={!!isLoading}
                isDisabled={!!isLoading}
                loadingText="Processing..."
              >
                Continue
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
